import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { navigateToUrl } from 'single-spa'
import { TaskCardRow } from '../components/TaskCardRow'
import { ArrowIcon, Button, CarDamageIcon, CardIcon, Heading, PlusCircleIcon, Stack } from '@autoplan/web-ui'
import { InformationCard } from '../components/Cards/InformationCard'
import { MyTaskCard } from '../components/Cards/MyTaskCard'
import { NewsFeaturedCard } from '../components/Cards/NewsFeaturedCard'
import { NewsCard } from '../components/Cards/NewsCard'
import { useHomeContent, useLocale, urlFor, getPortableTextExcerpt, type HomeContent } from '@autoplan/web-sanity'
import { localize } from '@autoplan/common'
import { useUserSummary } from '../hooks/useUserSummary'
import { useUser } from '@_shared/context/UserProvider'
import { TASK_TAB_PARAM, type TaskType, WEB_MODULES } from '@_shared/types'

type InfoCard = HomeContent['infoCards'][number]

const INFO_CARD_ICONS: Record<InfoCard['actionType'], ReactNode> = {
  orderCar: <PlusCircleIcon />,
  reportDamage: <CarDamageIcon />,
  lostCard: <CardIcon />,
  link: <ArrowIcon />,
}

// Damage reports and lost fuel cards are handled per car, so those cards lead to the car park
function openInfoCard({ actionType, url }: InfoCard) {
  if (actionType !== 'link') return navigateToUrl('/cars')
  if (!url) return
  if (url.startsWith('/')) return navigateToUrl(url)
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const Home = () => {
  const navigate = useNavigate()
  const content = useHomeContent()
  const { canAccess } = useUser()
  const { userSummary } = useUserSummary(canAccess(WEB_MODULES.TASKS))
  const hasTasks = !!(userSummary?.readyForSigning || userSummary?.sentForSigning || userSummary?.selections)
  const { lang, t } = useLocale()

  const page = content?.page ?? null
  const featuredArticle = content?.featuredArticle ?? null
  const articles = content?.articles ?? ([] as HomeContent['articles'])
  const infoCards = content?.infoCards ?? []

  return (
    <Stack spacing="4xl">
      <TaskCardRow />
      <div className="ap:grid ap:grid-cols-1 ap:gap-2xl ap:xl:grid-cols-2 ap:xl:items-start ap:xl:gap-3xl">
        <div className="ap:xl:col-start-2 ap:xl:row-start-1">
          {featuredArticle && (
            <NewsFeaturedCard
              title={featuredArticle.title ?? ''}
              imageSrc={
                featuredArticle.image
                  ? (urlFor(featuredArticle.image)?.width(800).height(440).url() ?? undefined)
                  : undefined
              }
              onClick={() => featuredArticle.slug && navigate(`/articles/${featuredArticle.slug.current}`)}
            />
          )}
        </div>

        <Stack spacing="2xl" className="ap:xl:col-start-1 ap:xl:row-start-1 ap:xl:row-span-2">
          {hasTasks && (
            <Stack spacing="xl">
              <Heading size="md">{t('home.tasks.title')}</Heading>
              {!!userSummary?.readyForSigning && (
                <MyTaskCard
                  label={t('home.tasks.readyForSigning')}
                  subLabel={t('home.tasks.readyForSigningDescription')}
                  taskCount={userSummary.readyForSigning}
                  href={tasksHref('ready-for-signing')}
                />
              )}
              {!!userSummary?.sentForSigning && (
                <MyTaskCard
                  label={t('home.tasks.sentForSigning')}
                  subLabel={t('home.tasks.sentForSigningDescription')}
                  taskCount={userSummary.sentForSigning}
                  href={tasksHref('sent-for-signing')}
                />
              )}
              {!!userSummary?.selections && (
                <MyTaskCard
                  label={t('home.tasks.selections')}
                  subLabel={t('home.tasks.selectionsDescription')}
                  taskCount={userSummary.selections}
                  href={tasksHref('selections')}
                />
              )}
            </Stack>
          )}

          {infoCards.length > 0 && (
            <Stack spacing="xl">
              <Heading size="md">{t('home.information.title')}</Heading>
              {infoCards.map((card) => (
                <InformationCard
                  key={card._id}
                  icon={INFO_CARD_ICONS[card.actionType]}
                  label={localize(card.label, lang)}
                  buttonLabel={localize(card.buttonLabel, lang)}
                  onClick={() => openInfoCard(card)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {articles.length > 0 && (
          <Stack spacing="2xl" className="ap:xl:col-start-2 ap:xl:row-start-2">
            <Stack spacing="xl">
              <Heading size="lg">{localize(page?.newsHeading, lang)}</Heading>
              {articles.map((article: HomeContent['articles'][number]) => (
                <NewsCard
                  key={article._id}
                  title={article.title ?? ''}
                  introduction={getPortableTextExcerpt(article.body)}
                  imageSrc={
                    article.image ? (urlFor(article.image)?.width(300).height(150).url() ?? undefined) : undefined
                  }
                  onClick={() => article.slug && navigate(`/articles/${article.slug.current}`)}
                />
              ))}
              <Button variant="primary" onClick={() => navigate('/articles')}>
                {t('common.actions.seeMore')}
              </Button>
            </Stack>
          </Stack>
        )}
      </div>
    </Stack>
  )
}

function tasksHref(type: TaskType) {
  return `/tasks?${TASK_TAB_PARAM}=${type}`
}
